// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from '@imprivata-cloud/common';
import type { Patient } from '../../../models/patient';
import { useEffect } from 'react';
import { i18n } from '../../../i18n';

export interface EnrollPageError {
  message?: string;
  instructions?: string;
  type?: 'error' | 'warning';
}

interface EnrollPageErrorHandlingProps {
  createEnrollmentError: ApiErrorPayloadCommon | undefined | null;
  patientError: (ApiErrorPayloadCommon & { cause?: string }) | undefined | null;
  patient: Patient | undefined;
  isPatientLoading: boolean;
  inquiryError: ApiErrorPayloadCommon | undefined | null;
  error: EnrollPageError | undefined;
  grantStatus: { pendingAuthorization?: boolean; pendingHandoff?: boolean } | undefined;
  isGrantStatusLoading: boolean;
  onSetEnrollmentSuccess: (success: boolean) => void;
  onSetError: (error: EnrollPageError | undefined) => void;
  onEnablePatientQuery: () => void;
  onEnableGrantStatusQuery: () => void;
  onSetShowPatientAlreadyEnrolledError: (show: boolean) => void;
  onSetShowPhoto: (show: boolean) => void;
}

export const useEnrollPageErrorHandling = ({
  createEnrollmentError,
  patientError,
  patient,
  isPatientLoading,
  inquiryError,
  error,
  grantStatus,
  isGrantStatusLoading,
  onSetEnrollmentSuccess,
  onSetError,
  onEnablePatientQuery,
  onEnableGrantStatusQuery,
  onSetShowPatientAlreadyEnrolledError,
  onSetShowPhoto,
  t: translateOverride,
}: EnrollPageErrorHandlingProps & { t?: (key: string) => string }) => {
  // Allow injection of translator so hook can return final strings for tests
  // Fallback to global i18n instance so tests without injected translator still receive translated strings.
  const t = translateOverride ?? ((k: string) => i18n.t(k));
  const enrollmentFailedMsg = t('enrollPage.enrollment-failed');
  const identityVerificationFailedMsg = t('enrollPage.identity-verification-failed');
  const defaultRemoteEnrollErrorInstructions = t(
    'enrollPage.default-remote-enroll-error-instructions',
  );
  const defaultAccountCreationErrorInstructions = t(
    'enrollPage.default-account-creation-error-instructions',
  );
  const getErrorInstructions = (customInstructions?: string) => {
    // When pendingAuthorization is true, use specific instructions based on error type
    if (grantStatus?.pendingAuthorization) {
      if (createEnrollmentError) {
        if (createEnrollmentError.error.code === 'low-quality') {
          return t('enrollPage.try-again-from-portal');
        }
        if (grantStatus?.pendingHandoff) {
          return t('enrollPage.unable-to-create-account-return-device');
        }
        return t('enrollPage.unable-to-create-account-return-mychart');
      }
      if (inquiryError) {
        if (grantStatus?.pendingHandoff) {
          return t('enrollPage.unable-to-create-account-return-original-device');
        }
        return defaultAccountCreationErrorInstructions;
      }
    }

    // When pendingAuthorization is false, return original custom instruction
    if (customInstructions) {
      return customInstructions;
    }
    return defaultRemoteEnrollErrorInstructions;
  };

  const setError = (message: string, instructions?: string, errorType?: 'error' | 'warning') => {
    onSetError({
      message,
      instructions: getErrorInstructions(instructions),
      type: errorType || 'error',
    });
  };

  // Handle createEnrollmentError - wait for grant status before setting error
  useEffect(() => {
    if (createEnrollmentError) {
      onSetEnrollmentSuccess(false);
      onEnableGrantStatusQuery();
      if (!isGrantStatusLoading) {
        switch (createEnrollmentError.error.code) {
          case 'already-exists':
            setError(enrollmentFailedMsg);
            onEnablePatientQuery();
            onSetShowPhoto(true);
            break;
          case 'argument-validation-failed':
            setError(
              t('enrollPage.unable-to-verify-your-identity'),
              t('enrollPage.try-again-portal-person-close-window'),
              'warning',
            );
            onSetShowPhoto(false);
            break;
          case 'low-quality':
            setError(
              t('enrollPage.photo-quality-too-low'),
              t('enrollPage.try-again-portal-person-close-window'),
              'warning',
            );
            onSetShowPhoto(false);
            break;
          case 'multiple-matches-found':
            onSetShowPatientAlreadyEnrolledError(true);
            onSetShowPhoto(false);
            break;
          case 'duplicate-found-with-different-identifier':
            setError(
              t('enrollPage.unable-to-complete-enrollment'),
              t('enrollPage.unable-to-complete-enrollment-at-this-time'),
              'warning',
            );
            onSetShowPhoto(false);
            break;
          case 'session-not-found':
          case 'missing-handoff-token':
          case 'invalid-session-id':
            setError(
              t('enrollPage.session-expired'),
              t('enrollPage.session-timed-out-for-security'),
            );
            onSetShowPhoto(false);
            break;
          case 'missing-tenant-id':
            setError(
              t('enrollPage.invalid-request'),
              t(
                'errorComponent.start-from-your-patient-portal-continue-you-may-close-this-window',
              ),
            );
            onSetShowPhoto(false);
            break;
          default:
            setError(enrollmentFailedMsg);
            onSetShowPhoto(false);
            break;
        }
      }
    }
  }, [createEnrollmentError, isGrantStatusLoading]);

  // Handle patientError
  useEffect(() => {
    if (patientError?.cause === 'not-found') {
      onSetEnrollmentSuccess(false);
      setError(enrollmentFailedMsg);
    }
  }, [patientError]);

  // Handle inquiryError - wait for grant status before setting error
  useEffect(() => {
    onEnableGrantStatusQuery();
    if (inquiryError && !isGrantStatusLoading) {
      if (inquiryError.error.code === 'missing-tenant-id') {
        setError(
          t('enrollPage.invalid-request'),
          t('errorComponent.start-from-your-patient-portal-continue-you-may-close-this-window'),
        );
      } else {
        setError(identityVerificationFailedMsg);
      }
    }
  }, [inquiryError, isGrantStatusLoading, grantStatus]);

  // Validate enrollment success
  useEffect(() => {
    if (patient && !isPatientLoading && !patientError) {
      onSetEnrollmentSuccess(true);
    }
  }, [patient, isPatientLoading, patientError]);

  // Hide photo when error message is set
  useEffect(() => {
    if (error?.message) {
      onSetShowPhoto(false);
    }
  }, [error?.message]);
};
